import React from "react";
import { TrendingUp, TrendingDown } from 'lucide-react';

/**
 * StatCard - Enterprise-grade stat display card with mobile optimization
 * 
 * @param {string|number} value - Main stat value
 * @param {string} label - Description text
 * @param {React.Component} icon - Lucide icon component
 * @param {string} color - Tailwind class for value color
 * @param {string} bg - Custom background classes
 * @param {function} onClick - Optional click handler
 * @param {string} size - Size variant: 'sm', 'md', 'lg'
 * @param {string} trend - Trend direction: 'up', 'down', 'neutral'
 * @param {string|number} trendValue - Optional trend value to display
 * @param {string} variant - Visual variant: 'default', 'gradient', 'glass'
 */
const StatCard = ({ 
  value, 
  label, 
  icon: Icon, 
  color = "text-cream-900 dark:text-white",
  bg,
  onClick,
  size = 'md',
  trend,
  trendValue,
  variant = 'default',
  subtitle
}) => {
  const sizes = {
    sm: {
      padding: 'p-4',
      icon: 'w-5 h-5',
      value: 'text-xl',
      label: 'text-xs',
      iconBg: 'p-2'
    },
    md: {
      padding: 'p-5 sm:p-6',
      icon: 'w-6 h-6',
      value: 'text-2xl sm:text-3xl',
      label: 'text-sm',
      iconBg: 'p-2.5'
    },
    lg: {
      padding: 'p-6 sm:p-8',
      icon: 'w-7 h-7',
      value: 'text-3xl sm:text-4xl',
      label: 'text-base',
      iconBg: 'p-3'
    }
  };

  const variants = {
    default: {
      // Was bg-cream-200 (#E9E1F9, a real light violet) as the DEFAULT
      // fill -- since "default" is this component's most common variant,
      // that meant most stat cards app-wide rendered as a solid light-
      // purple block rather than the intended white/neutral surface with
      // violet reserved for accents. cream-200 stays reserved for genuine
      // secondary/hover panels (see `glass.hover` below), not a primary
      // card fill. Confirmed live via screenshot, 2026-09-11.
      base: bg || "bg-cream-100 dark:bg-gray-800/50",
      border: "border border-cream-300 dark:border-gray-700/50",
      hover: "hover:bg-cream-200 dark:hover:bg-gray-800/70 hover:border-cream-300 dark:hover:border-gray-600/60"
    },
    gradient: {
      base: bg || "bg-gradient-to-br from-blue-100 to-violet-100 dark:from-blue-600/20 dark:to-purple-600/20",
      border: "border border-blue-300 dark:border-blue-500/30",
      hover: "hover:from-blue-200 dark:hover:from-blue-600/30 hover:to-violet-200 dark:hover:to-purple-600/30 hover:border-blue-400 dark:hover:border-blue-500/50"
    },
    glass: {
      base: bg || "bg-cream-100 dark:bg-gray-800/30 backdrop-blur-xl",
      border: "border border-cream-300 dark:border-gray-700/30",
      hover: "hover:bg-cream-200 dark:hover:bg-gray-800/50 hover:border-cream-300 dark:hover:border-gray-600/50"
    }
  };

  const trendColors = {
    up: 'text-green-700 dark:text-green-400',
    down: 'text-red-700 dark:text-red-400',
    neutral: 'text-cream-700 dark:text-gray-400'
  };

  const sizeClasses = sizes[size] || sizes.md;
  const variantClasses = variants[variant] || variants.default;

  const TrendIcon = trend === 'up' ? TrendingUp : trend === 'down' ? TrendingDown : null;

  return (
    <div
      className={`
        flex flex-col rounded-2xl shadow-lg transition-all duration-300 min-w-0
        ${sizeClasses.padding}
        ${variantClasses.base}
        ${variantClasses.border}
        ${onClick ? `cursor-pointer ${variantClasses.hover} hover:scale-[1.02] active:scale-[0.98] hover:shadow-xl` : ""}
      `}
      onClick={onClick}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      } : undefined}
    >
      {/* Icon */}
      {Icon && (
        <div className={`
          ${sizeClasses.iconBg} rounded-xl mb-4
          bg-gradient-to-br from-blue-100 to-violet-100 dark:from-blue-600/20 dark:to-purple-600/20
          border border-blue-300 dark:border-blue-500/20 backdrop-blur-sm
          inline-flex items-center justify-center
          w-fit
        `}>
          <Icon className={`${sizeClasses.icon} text-blue-700 dark:text-blue-400`} />
        </div>
      )}

      {/* Value and trend */}
      <div className="flex items-baseline gap-3 mb-2">
        <div className={`${sizeClasses.value} font-bold ${color} leading-none`}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </div>
        
        {trend && TrendIcon && (
          <div className="flex items-center gap-1">
            <TrendIcon className={`w-4 h-4 ${trendColors[trend]}`} />
            {trendValue && (
              <span className={`text-sm font-semibold ${trendColors[trend]}`}>
                {trendValue}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Label -- was `truncate` (forced single line + ellipsis) which cut
          off longer labels like "Open Predictions" into "Open Predict..."
          on both mobile and desktop regardless of actual available width;
          letting it wrap to two lines instead means the real word is
          always readable. */}
      <div className={`${sizeClasses.label} text-cream-700 dark:text-gray-400 font-medium leading-snug`}>
        {label}
      </div>

      {/* Optional subtitle */}
      {subtitle && (
        <div className="text-xs text-cream-700 dark:text-gray-500 mt-1">
          {subtitle}
        </div>
      )}
    </div>
  );
};

export default StatCard;